import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import {
  Button, Input, Modal, Upload, message, Table, Space, Form, Popconfirm, Select
} from 'antd';
import {
  UploadOutlined, PlusOutlined, EyeOutlined,
  EditOutlined, DeleteOutlined, TagsOutlined
} from '@ant-design/icons';

// ── Section card component ─────────────────────────────────────
const SectionCard = ({ title, icon, count, children }) => (
  <div className="section-card">
    <div className="section-header">
      <span className="section-header-icon">{icon}</span>
      <span className="section-header-title">{title}</span>
      {count !== undefined && <span className="section-badge">{count}</span>}
    </div>
    <div>{children}</div>
  </div>
);

// ── Main component ─────────────────────────────────────────────
const CategoryManager = () => {
  const [form]             = Form.useForm();
  const [categories,       setCategories]       = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [editingCategory,  setEditingCategory]  = useState(null);
  const [fileList,         setFileList]         = useState([]);
  const [isModalOpen,      setIsModalOpen]      = useState(false);
  const [submitLoading,    setSubmitLoading]    = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const [parentsRes, allRes] = await Promise.all([
        axiosInstance.get('/auth/categories/all-parent'),
        axiosInstance.get('/vendor/categories'),
      ]);
      setParentCategories(parentsRes.data);
      setCategories(allRes.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to fetch categories');
    }
    setLoading(false);
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleFormSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      if (values.description)    formData.append('description',    values.description);
      if (values.parentCategory) formData.append('parentCategory', values.parentCategory);
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('image', fileList[0].originFileObj);
      }
      if (editingCategory) {
        await axiosInstance.put(`/auth/admin/categories/${editingCategory._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Category updated successfully');
      } else {
        await axiosInstance.post('/auth/admin/categories', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Category created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingCategory(null);
      setFileList([]);
      fetchCategories();
    } catch (error) {
      message.error(error?.response?.data?.message || 'Error saving category');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name:           category.name,
      description:    category.description,
      parentCategory: category.parentCategory?._id,
    });
    if (category.image?.url) {
      setFileList([{
        uid: '-1',
        name: category.image.fileName || 'image.png',
        status: 'done',
        url: category.image.url,
      }]);
    } else {
      setFileList([]);
    }
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/auth/admin/categories/${id}`);
      message.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      message.error(error?.response?.data?.message || 'Error deleting category');
    }
  };

  const handleView = async (id) => {
    try {
      const res = await axiosInstance.get(`/auth/categories/${id}`);
      Modal.info({
        title: res.data.name,
        content: (
          <div style={{ paddingTop: 8 }}>
            {[
              ['Slug',        res.data.slug],
              ['Description', res.data.description || 'No description'],
              ['Parent',      res.data.parentCategory?.name || 'None (Parent Category)'],
              ['Created By',  res.data.createdBy || 'Unknown'],
            ].map(([label, val]) => (
              <div key={label} style={{
                display: 'flex', gap: 10, marginBottom: 8,
                padding: '8px 12px',
                background: 'var(--or-bg)',
                borderRadius: 'var(--r)',
                borderLeft: '3px solid var(--or)',
              }}>
                <span style={{ fontWeight: 600, color: 'var(--or)', minWidth: 90 }}>{label}:</span>
                <span style={{ color: 'var(--tx)' }}>{val}</span>
              </div>
            ))}
            {res.data.image?.url && (
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <img
                  src={res.data.image.url}
                  alt="category"
                  style={{
                    maxWidth: 180,
                    borderRadius: 'var(--r-lg)',
                    border: '1px solid var(--bdr)',
                    boxShadow: 'var(--sh)',
                  }}
                />
              </div>
            )}
          </div>
        ),
        width: 480,
      });
    } catch {
      message.error('Failed to load category details');
    }
  };

  const beforeUpload = (file) => {
    if (!file.type.startsWith('image/')) { message.error('Image files only!'); return false; }
    if (file.size / 1024 / 1024 >= 5)   { message.error('Image must be < 5 MB'); return false; }
    return false;
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setFileList([]);
    setEditingCategory(null);
  };

  const openAddModal = () => {
    setEditingCategory(null);
    form.resetFields();
    setFileList([]);
    setIsModalOpen(true);
  };

  // ── Table columns ──────────────────────────────────────────────
  const parentCategoryColumns = [
    {
      title: 'Category', dataIndex: 'name', key: 'name',
      render: t => <span style={{ fontWeight: 500 }}>{t}</span>,
    },
    {
      title: 'Slug', dataIndex: 'slug', key: 'slug',
      render: t => <span style={{ color: 'var(--tx2)', fontSize: 12 }}>{t}</span>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space size={6}>
          <Popconfirm
            title="Delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes" cancelText="No"
          >
            <Button size="small" className="action-btn-delete" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const subcategoryColumns = [
    {
      title: 'Category', dataIndex: 'name', key: 'name',
      render: t => <span style={{ fontWeight: 500 }}>{t}</span>,
    },
    {
      title: 'Slug', dataIndex: 'slug', key: 'slug',
      render: t => <span style={{ color: 'var(--tx2)', fontSize: 12 }}>{t}</span>,
    },
    {
      title: 'Parent', key: 'parent',
      render: (_, r) => (
        <span className="badge badge-orange">{r.parentCategory?.name || '—'}</span>
      ),
    },
    {
      title: 'Image', key: 'image',
      render: (_, r) => r.image?.url
        ? <img src={r.image.url} width={36} height={36} alt="cat"
            style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid var(--bdr)' }} />
        : <span style={{ color: 'var(--tx3)', fontSize: 12 }}>—</span>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space size={6}>
          <Button size="small" className="action-btn-view"  icon={<EyeOutlined />}    onClick={() => handleView(record._id)} />
          <Button size="small" className="action-btn-edit"  icon={<EditOutlined />}   onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes" cancelText="No"
          >
            <Button size="small" className="action-btn-delete" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const parents = categories.filter(c => !c.parentCategory);
  const subs    = categories.filter(c =>  c.parentCategory);

  return (
    <div className="category-page">

      {/* Page header */}
      <div className="ph">
        <div>
          <h1>Categories</h1>
          <p>Manage your e-commerce categories &amp; subcategories</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus" /> Add Category
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="cat-stats">
        {[
          { label: 'Total Categories',  val: categories.length },
          { label: 'Parent Categories', val: parents.length },
          { label: 'Subcategories',     val: subs.length },
        ].map(s => (
          <div key={s.label} className="cat-stat-card">
            <div className="cat-stat-icon">
              <TagsOutlined />
            </div>
            <div>
              <div className="cat-stat-val">{s.val}</div>
              <div className="cat-stat-lbl">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Parent categories table */}
      <SectionCard title="Parent Categories" icon={<TagsOutlined />} count={parents.length}>
        <Table
          className="cat-table"
          columns={parentCategoryColumns}
          dataSource={parents}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="middle"
        />
      </SectionCard>

      {/* Subcategories table */}
      <SectionCard title="Subcategories" icon={<TagsOutlined />} count={subs.length}>
        <Table
          className="cat-table"
          columns={subcategoryColumns}
          dataSource={subs}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 15 }}
          size="middle"
        />
      </SectionCard>

      {/* Add / Edit Modal */}
      <Modal
        className="cat-modal"
        open={isModalOpen}
        onCancel={handleModalCancel}
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        onOk={() => form.submit()}
        okText={editingCategory ? 'Update' : 'Create'}
        confirmLoading={submitLoading}
        destroyOnClose
        width={540}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit} style={{ paddingTop: 4 }}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { min: 2, message: 'At least 2 characters' },
            ]}
          >
            <Input placeholder="e.g. Electronics, Clothing…" size="large" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: 'Max 500 characters' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Short description (optional)"
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item name="parentCategory" label="Parent Category">
            <Select allowClear placeholder="Select parent (optional)" size="large">
              {parentCategories
                .filter(c => c._id !== editingCategory?._id)
                .map(c => (
                  <Select.Option key={c._id} value={c._id}>{c.name}</Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item label="Category Image">
            <Upload
              fileList={fileList}
              listType="picture-card"
              onChange={({ fileList: fl }) => setFileList(fl)}
              beforeUpload={beforeUpload}
              onRemove={() => setFileList([])}
              accept="image/*"
              maxCount={1}
            >
              {fileList.length === 0 && (
                <div>
                  <UploadOutlined style={{ fontSize: 18, color: 'var(--or)' }} />
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--or)', fontWeight: 600 }}>
                    Upload
                  </div>
                </div>
              )}
            </Upload>
            <p style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4 }}>
              Max 5 MB · JPG, PNG, GIF, WebP
            </p>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManager;